// Sums observed obligations by their declared unit. Units are never converted between each other,
// so a USD total and a BRL total are separate facts rather than one market-cap figure.

const FIAT_UNITS = new Set(['USD', 'EUR', 'GBP', 'SGD', 'BRL', 'AUD', 'CHF', 'JPY', 'MXN', 'CAD', 'NZD', 'HKD', 'ARS']);

export function isFiatUnit(unit) {
  return FIAT_UNITS.has(String(unit || '').toUpperCase());
}

export function aggregateByUnit(supplies, { maxSegments = 5 } = {}) {
  const buckets = new Map();
  for (const row of supplies) {
    if (row.source === 'xrplmeta' || row.excludeFromActive) continue;
    if (!isFiatUnit(row.unit)) continue;
    const value = Number(row.circulating) || 0;
    if (value <= 0) continue;
    const unit = row.unit.toUpperCase();
    const bucket = buckets.get(unit) || { unit, total: 0, instruments: [] };
    bucket.total += value;
    bucket.instruments.push({
      key: row.key,
      programId: row.programId,
      ticker: row.ticker,
      programName: row.programName,
      value,
    });
    buckets.set(unit, bucket);
  }

  return [...buckets.values()]
    .map((bucket) => {
      const sorted = bucket.instruments.sort((a, b) => b.value - a.value);
      const head = sorted.slice(0, maxSegments);
      const tail = sorted.slice(maxSegments);
      const segments = head.map((item) => ({ ...item, share: item.value / bucket.total }));
      if (tail.length) {
        const value = tail.reduce((sum, item) => sum + item.value, 0);
        segments.push({ key: `${bucket.unit}:other`, ticker: `${tail.length} others`, programName: null, value, share: value / bucket.total, other: true });
      }
      return { ...bucket, instrumentCount: sorted.length, segments, leader: sorted[0] };
    })
    .sort((a, b) => b.instruments.length - a.instruments.length || b.total - a.total);
}

// Buckets flows into per-minute counts for the trailing window, oldest first.
export function flowCadence(flows, { minutes = 30, now = Date.now() } = {}) {
  const buckets = Array.from({ length: minutes }, (_, index) => ({
    minuteStart: now - (minutes - index) * 60_000,
    count: 0,
  }));
  for (const flow of flows) {
    if (flow.source !== 'live' || flow.date === null || flow.date === undefined) continue;
    const timestamp = (Number(flow.date) + 946_684_800) * 1000;
    const offset = Math.floor((now - timestamp) / 60_000);
    if (offset < 0 || offset >= minutes) continue;
    buckets[minutes - 1 - offset].count += 1;
  }
  return buckets;
}
